/**
 * AddToHomeScreen — install nudge for mobile visitors.
 * Android/Chrome: native install via beforeinstallprompt.
 * iOS Safari: one-time instruction hint (Share → Add to Home Screen).
 * Shows once per 30 days if dismissed; never when already installed.
 */
import React, { useEffect, useState } from 'react';
import { X, Share, PlusSquare, Download } from 'lucide-react';

const SNOOZE_KEY = 'hibff_a2hs_snoozed_until';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

export const AddToHomeScreen = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (!(localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token'))) return;
    const snoozed = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < snoozed) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    let t;
    if (isIOS()) {
      t = setTimeout(() => { setIosMode(true); setShow(true); }, 8000);
    }
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); if (t) clearTimeout(t); };
  }, []);

  const dismiss = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + 30 * 24 * 3600 * 1000));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome !== 'accepted') localStorage.setItem(SNOOZE_KEY, String(Date.now() + 30 * 24 * 3600 * 1000));
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-[60] animate-slideUp" data-testid="a2hs-banner">
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 pr-10">
        <button onClick={dismiss} className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-gray-100" aria-label="Dismiss" data-testid="a2hs-dismiss">
          <X size={16} style={{ color: '#9CA3AF' }} />
        </button>
        <div className="flex items-start gap-3">
          <img src="/favicon-96.png" alt="HiBFF" className="w-11 h-11 rounded-xl" style={{ border: '1px solid #E5E7EB' }} />
          <div className="min-w-0">
            <p className="font-black text-sm" style={{ color: '#111827' }}>Add HiBFF to your Home Screen</p>
            {iosMode ? (
              <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
                Tap <Share size={12} className="inline -mt-0.5" /> <b>Share</b>, then <PlusSquare size={12} className="inline -mt-0.5" /> <b>Add to Home Screen</b> — HiBFF opens like an app, straight to your practice sessions.
              </p>
            ) : (
              <>
                <p className="text-xs mt-1" style={{ color: '#6B7280' }}>One tap and HiBFF opens like an app — straight to your practice sessions.</p>
                <button onClick={install} data-testid="a2hs-install-btn"
                  className="mt-2 px-4 py-2 rounded-full text-xs font-bold text-white flex items-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg,#F5831F 0%,#FF6B35 70%)' }}>
                  <Download size={13} /> Install HiBFF
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddToHomeScreen;
